import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AlertCircle, Ban, Copy, DollarSign, Download, Edit, Mail, Printer, Send, Trash2 } from 'lucide-react';
import AttachmentsListView from '../../components/AttachmentsListView';
import InvoiceSendModal from '../../components/InvoiceSendModal';
import VoidConfirmationModal from '../../components/VoidConfirmationModal';
import TransactionOverlay from '../../components/transactions/TransactionOverlay';
import {
  deleteInvoice,
  getInvoiceById,
  postInvoice,
  voidInvoice,
  copyInvoice,
  downloadInvoicePdf,
} from '../../utils/invoiceApi';
import { getCustomerPayments } from '../../utils/customerPaymentApi';
import { printSalesInvoice } from '../../utils/printSalesInvoice';
import type { Invoice, InvoiceStatus } from '../../types/invoice';
import type { CustomerPayment } from '../../types/customerPayment';
import type { Attachment } from '../../types/attachment';
import { getInvoiceAttachments } from '../../utils/itemAttachmentApi';
import { parseInventoryQty } from '../../utils/negativeInventory';
import {
  canCopyInvoice,
  canDeleteDraftInvoice,
  canEditInvoiceTransaction,
  canManageInvoices,
  canPostInvoice,
  canReceivePaymentForInvoice,
  canVoidInvoice,
  isPaymentVoided,
} from '../../utils/salesTransactionPermissions';
import { toast } from '../../utils/toast';
import { invoiceVoidCopy } from '../../utils/voidConfirmationCopy';
import { useInventoryTransactionsHub } from './InventoryTransactionsHubContext';
import {
  parseInventoryHubLocationState,
  invoiceEditFromPreviewState,
  receivePaymentFromPreviewState,
} from './inventoryHubNavigation';
import {
  PreviewActionFooter,
  PreviewCollapsibleLineItems,
  PreviewFinancialHero,
  PreviewLinkedList,
  PreviewMetaGrid,
  PreviewMetaItem,
  PreviewSectionHeading,
  PreviewSkeleton,
  previewActionClasses,
} from '../../components/transactions/TransactionPreviewShared';

const formatLabel = (value: string) =>
  value
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString();

const getStatusColor = (status: InvoiceStatus) => {
  switch (status) {
    case 'DRAFT':
      return 'bg-gray-100 text-gray-700';
    case 'OPEN':
      return 'bg-blue-100 text-blue-800';
    case 'PARTIAL':
      return 'bg-yellow-100 text-yellow-800';
    case 'PAID':
      return 'bg-green-100 text-green-800';
    case 'VOIDED':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const customerName = (invoice: Invoice) =>
  invoice.customer_snapshot.name || invoice.customer?.display_name || '—';

type InvoicePaymentRow = {
  paymentId: number;
  paymentNumber: string;
  txnDate: string;
  paymentMethod: CustomerPayment['payment_method'];
  amountApplied: string;
};

const invoiceHasPayments = (invoice: Pick<Invoice, 'status'>) =>
  invoice.status === 'PAID' || invoice.status === 'PARTIAL';

const extractInvoicePayments = (
  invoiceId: number,
  payments: CustomerPayment[]
): InvoicePaymentRow[] =>
  payments
    .filter((payment) => !isPaymentVoided(payment))
    .flatMap((payment) =>
      payment.allocations
        .filter((alloc) => alloc.invoice_id === invoiceId)
        .map((alloc) => ({
          paymentId: payment.id,
          paymentNumber: payment.payment_number,
          txnDate: payment.txn_date,
          paymentMethod: payment.payment_method,
          amountApplied: alloc.amount_applied,
        }))
    )
    .sort((a, b) => a.txnDate.localeCompare(b.txnDate) || a.paymentId - b.paymentId);

const InventoryInvoicePreview = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { refetchList } = useInventoryTransactionsHub();
  const listSearch = location.search.replace(/^\?/, '');
  const transactionId = parseInventoryHubLocationState(location.state)?.transactionId;

  const invoiceId = id != null ? Number(id) : Number.NaN;

  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [invoicePayments, setInvoicePayments] = useState<InvoicePaymentRow[]>([]);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [voidError, setVoidError] = useState<string | null>(null);
  const [voiding, setVoiding] = useState(false);

  const close = useCallback(() => {
    navigate({ pathname: '/inventory-transactions', search: listSearch }, { preventScrollReset: true });
  }, [listSearch, navigate]);

  const loadInvoice = useCallback(async () => {
    if (!id || Number.isNaN(invoiceId)) {
      setError('Invalid invoice ID');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const [data, invoiceAttachments] = await Promise.all([
        getInvoiceById(invoiceId),
        getInvoiceAttachments(invoiceId).catch(() => []),
      ]);
      let paymentsForInvoice: InvoicePaymentRow[] = [];
      if (invoiceHasPayments(data)) {
        const customerPayments = await getCustomerPayments({ customer_id: data.customer_id }).catch(
          () => []
        );
        paymentsForInvoice = extractInvoicePayments(invoiceId, customerPayments);
      }
      setInvoice(data);
      setInvoicePayments(paymentsForInvoice);
      setAttachments(invoiceAttachments);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load invoice');
      setInvoice(null);
      setInvoicePayments([]);
      setAttachments([]);
    } finally {
      setLoading(false);
    }
  }, [id, invoiceId]);

  useEffect(() => {
    void loadInvoice();
  }, [loadInvoice]);

  const openEdit = () => {
    if (!invoice || !canEditInvoiceTransaction(invoice)) return;
    navigate(
      { pathname: `/inventory-transactions/invoice/${invoice.id}`, search: listSearch },
      {
        state: invoiceEditFromPreviewState(invoice.id, transactionId),
        preventScrollReset: true,
      }
    );
  };

  const openPayment = (paymentId: number) => {
    const returnTo = `/inventory-transactions/view/invoice/${invoice?.id}${
      listSearch ? `?${listSearch}` : ''
    }`;
    navigate(
      {
        pathname: `/sales-transactions/view/payment/${paymentId}`,
        search: listSearch,
      },
      { preventScrollReset: true, state: { returnTo } }
    );
  };

  const handlePost = async () => {
    if (!invoice || !canPostInvoice(invoice)) return;
    if (!window.confirm(`Post invoice ${invoice.invoice_number}? This will affect inventory and GL.`)) {
      return;
    }

    try {
      setActionLoading(true);
      await postInvoice(invoice.id);
      refetchList();
      const updated = await getInvoiceById(invoice.id);
      setInvoice(updated);
      toast.success(`Invoice ${invoice.invoice_number} posted.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to post invoice');
    } finally {
      setActionLoading(false);
    }
  };

  const openVoidModal = () => {
    setVoidError(null);
    setShowVoidModal(true);
  };

  const cancelVoidModal = () => {
    setShowVoidModal(false);
    setVoidError(null);
  };

  const confirmVoid = async () => {
    if (!invoice || !canVoidInvoice(invoice)) return;

    try {
      setVoiding(true);
      setVoidError(null);
      await voidInvoice(invoice.id);
      refetchList();
      const updated = await getInvoiceById(invoice.id);
      setInvoice(updated);
      setShowVoidModal(false);
      toast.success(`Invoice ${invoice.invoice_number} voided.`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to void invoice';
      setVoidError(message);
      toast.error(message);
    } finally {
      setVoiding(false);
    }
  };

  const handleReceivePayment = () => {
    if (!invoice || !canReceivePaymentForInvoice(invoice.status)) return;
    navigate(
      { pathname: '/inventory-transactions/payment/receive', search: listSearch },
      {
        state: receivePaymentFromPreviewState(
          invoice.id,
          invoice.customer_id,
          invoice.id,
          transactionId
        ),
        preventScrollReset: true,
      }
    );
  };

  const handleDelete = async () => {
    if (!invoice || !canDeleteDraftInvoice(invoice)) return;
    if (
      !window.confirm(`Delete draft ${invoice.invoice_number}? This cannot be undone.`)
    ) {
      return;
    }

    try {
      setActionLoading(true);
      await deleteInvoice(invoice.id);
      refetchList();
      close();
      toast.success(`Invoice ${invoice.invoice_number} deleted.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete invoice');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!invoice || !canCopyInvoice()) return;
    if (
      !window.confirm(
        `Copy invoice ${invoice.invoice_number}? A new draft will be created.`
      )
    ) {
      return;
    }

    try {
      setActionLoading(true);
      const copied = await copyInvoice(invoice.id);
      refetchList();
      toast.success(`Copied to ${copied.invoice_number}.`);
      navigate(
        { pathname: `/inventory-transactions/invoice/${copied.id}`, search: listSearch },
        {
          state: invoiceEditFromPreviewState(copied.id, transactionId),
          preventScrollReset: true,
        }
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to copy invoice');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePrint = async () => {
    if (!invoice) return;
    try {
      setActionLoading(true);
      await printSalesInvoice(invoice);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to print invoice');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!invoice) return;
    try {
      setActionLoading(true);
      await downloadInvoicePdf(invoice.id, `${invoice.invoice_number}.pdf`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to download invoice PDF');
    } finally {
      setActionLoading(false);
    }
  };

  const lines = invoice?.lines ?? [];
  const hasBackorderLines = lines.some((line) => parseInventoryQty(line.backorder_qty) > 0);
  const totalPaidOnInvoice = invoicePayments.reduce(
    (sum, row) => sum + parseFloat(row.amountApplied),
    0
  );
  const invoiceTotal = invoice ? parseFloat(invoice.total_amount) : 0;
  const invoiceBalance = invoice ? parseFloat(invoice.balance) : 0;
  const paidPercent =
    invoiceTotal > 0 ? Math.min(100, ((invoiceTotal - invoiceBalance) / invoiceTotal) * 100) : 0;

  const actionFooter =
    invoice && !loading && !error ? (
      <PreviewActionFooter>
        {canEditInvoiceTransaction(invoice) && (
          <button
            type="button"
            onClick={openEdit}
            disabled={actionLoading}
            className={previewActionClasses.primary}
          >
            <Edit className="h-4 w-4" />
            {invoice.status === 'DRAFT' ? 'Edit' : 'Amend'}
          </button>
        )}
        {canPostInvoice(invoice) && (
          <button
            type="button"
            onClick={handlePost}
            disabled={actionLoading}
            className={previewActionClasses.success}
          >
            <Send className="h-4 w-4" />
            Post
          </button>
        )}
        {canReceivePaymentForInvoice(invoice.status) && (
          <button
            type="button"
            onClick={handleReceivePayment}
            disabled={actionLoading}
            className={previewActionClasses.secondary}
          >
            <DollarSign className="h-4 w-4" />
            Receive payment
          </button>
        )}
        {canCopyInvoice() && (
          <button
            type="button"
            onClick={handleCopy}
            disabled={actionLoading}
            className={previewActionClasses.secondary}
          >
            <Copy className="h-4 w-4" />
            Copy
          </button>
        )}
        <button
          type="button"
          onClick={() => void handlePrint()}
          disabled={actionLoading}
          className={previewActionClasses.secondary}
        >
          <Printer className="h-4 w-4" />
          Print
        </button>
        <button
          type="button"
          onClick={() => void handleDownloadPdf()}
          disabled={actionLoading}
          className={previewActionClasses.secondary}
        >
          <Download className="h-4 w-4" />
          Download
        </button>
        {canManageInvoices() && (
          <button
            type="button"
            onClick={() => setShowSendModal(true)}
            disabled={actionLoading}
            className={previewActionClasses.secondary}
          >
            <Mail className="h-4 w-4" />
            Send
          </button>
        )}
        {canVoidInvoice(invoice) && (
          <button
            type="button"
            onClick={openVoidModal}
            disabled={actionLoading || voiding}
            className={previewActionClasses.danger}
          >
            <Ban className="h-4 w-4" />
            Void
          </button>
        )}
        {canDeleteDraftInvoice(invoice) && (
          <button
            type="button"
            onClick={handleDelete}
            disabled={actionLoading}
            className={previewActionClasses.dangerStrong}
          >
            <Trash2 className="h-4 w-4" />
            Delete draft
          </button>
        )}
        <button type="button" onClick={close} className={previewActionClasses.close}>
          Close
        </button>
      </PreviewActionFooter>
    ) : undefined;

  return (
    <>
    <TransactionOverlay
      variant="preview"
      titleMono
      title={invoice?.invoice_number ?? 'Invoice'}
      subtitle={
        invoice ? `${customerName(invoice)} · ${formatDate(invoice.txn_date)}` : 'Loading…'
      }
      footer={actionFooter}
      onClose={close}
    >
      {loading ? (
        <PreviewSkeleton />
      ) : error ? (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      ) : invoice ? (
        <div className="space-y-5">
          <div className="flex flex-wrap gap-2">
            <span
              className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}
            >
              {formatLabel(invoice.status)}
            </span>
            {invoice.allow_negative_stock && (
              <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                Oversell allowed
              </span>
            )}
          </div>

          <PreviewFinancialHero
            amounts={[
              { label: 'Total', amount: invoice.total_amount },
              {
                label: invoiceBalance > 0 ? 'Balance due' : 'Balance',
                amount: invoice.balance,
                tone: invoiceBalance > 0 ? 'owed' : 'paid',
              },
            ]}
            progressPercent={invoice.status === 'PARTIAL' ? paidPercent : undefined}
          />

          <PreviewMetaGrid>
            <PreviewMetaItem label="Customer">
              <span className="font-medium">{customerName(invoice)}</span>
            </PreviewMetaItem>
            {invoice.customer_snapshot.email && (
              <PreviewMetaItem label="Email">{invoice.customer_snapshot.email}</PreviewMetaItem>
            )}
            {invoice.customer_snapshot.phone && (
              <PreviewMetaItem label="Phone">{invoice.customer_snapshot.phone}</PreviewMetaItem>
            )}
            <PreviewMetaItem label="Due">{formatDate(invoice.due_date)}</PreviewMetaItem>
            <PreviewMetaItem label="Terms">{formatLabel(invoice.payment_terms)}</PreviewMetaItem>
          </PreviewMetaGrid>

          {invoiceHasPayments(invoice) && (
            <div>
              <PreviewSectionHeading>Payments applied</PreviewSectionHeading>
              {invoicePayments.length > 0 ? (
                <PreviewLinkedList
                  rows={invoicePayments.map((row) => ({
                    id: row.paymentId,
                    title: row.paymentNumber,
                    subtitle: `${formatDate(row.txnDate)} · ${formatLabel(row.paymentMethod)}`,
                    amount: row.amountApplied,
                    onClick: () => openPayment(row.paymentId),
                  }))}
                  footerLabel="Total paid"
                  footerAmount={String(totalPaidOnInvoice)}
                />
              ) : (
                <p className="text-xs text-gray-500 italic">Payment history unavailable.</p>
              )}
            </div>
          )}

          {lines.length > 0 && (
            <div>
              {hasBackorderLines && (
                <p className="mb-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                  Includes backordered lines — COGS may update when stock is received.
                </p>
              )}
              <PreviewCollapsibleLineItems
                lines={lines.map((line) => ({
                  id: line.id,
                  name: line.item_snapshot.name,
                  sku: line.item_snapshot.sku,
                  qty: line.qty,
                  unitPrice: line.unit_price,
                  lineTotal: line.line_total,
                }))}
              />
            </div>
          )}

          {invoice.memo && (
            <p className="text-sm text-gray-600">
              <span className="font-medium text-gray-700">Memo:</span> {invoice.memo}
            </p>
          )}

          {attachments.length > 0 && <AttachmentsListView attachments={attachments} />}
        </div>
      ) : null}
    </TransactionOverlay>

    {invoice && (
      <InvoiceSendModal
        invoice={invoice}
        isOpen={showSendModal}
        onClose={() => setShowSendModal(false)}
      />
    )}
    {invoice && (
      <VoidConfirmationModal
        isOpen={showVoidModal}
        {...invoiceVoidCopy(invoice.invoice_number)}
        isSubmitting={voiding}
        error={voidError}
        onCancel={cancelVoidModal}
        onConfirm={confirmVoid}
      />
    )}
    </>
  );
};

export default InventoryInvoicePreview;
